import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from './useTranslation';
import { loadLanguageAsync } from './i18n';

type LanguageContextType = {
    currentLanguage: string;
    changeLanguage: (lang: string) => Promise<string>;
    availableLanguages: { code: string; name: string }[];
    isLoading: boolean;
};

const LanguageContext = createContext<LanguageContextType>({
    currentLanguage: 'en',
    changeLanguage: () => Promise.resolve('en'),
    availableLanguages: [
        { code: 'en', name: 'English' },
        { code: 'es', name: 'Español' },
        { code: 'it', name: 'Italiano' },
        { code: 'de', name: 'Deutsch' },
        { code: 'fr', name: 'Français' },
        { code: 'ja', name: '日本語' },
        { code: 'ar', name: 'العربية' },
        { code: 'pt-br', name: 'Português (Brasil)' },
        { code: 'id', name: 'Indonesia' },
        { code: 'no', name: 'Norsk' },
    ],
    isLoading: false,
});

export const useLanguage = () => useContext(LanguageContext);

interface LanguageProviderProps {
    children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }: any) => {
    const { i18n } = useTranslation();
    const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');
    const [isLoading, setIsLoading] = useState(false);

    // List of available languages - can be extended as needed
    const knownLanguages = [
        { code: 'en', name: 'English' },
        { code: 'es', name: 'Español' },
        { code: 'it', name: 'Italiano' },
        { code: 'de', name: 'Deutsch' },
        { code: 'fr', name: 'Français' },
        { code: 'ja', name: '日本語' },
        { code: 'ar', name: 'العربية' },
        { code: 'pt-br', name: 'Português (Brasil)' },
        { code: 'id', name: 'Indonesia' },
        { code: 'no', name: 'Norsk' },
        // Add more languages here as they are created
    ];

    // Create a map of language codes to language objects for quick lookup
    const languageMap = new Map(knownLanguages.map((lang) => [lang.code, lang]));

    // Check if the current language is in our known languages list, if not add it
    const [availableLanguages, setAvailableLanguages] = useState(() => {
        if (currentLanguage !== 'en' && !languageMap.has(currentLanguage)) {
            // If we have an unknown language from a cookie, add it to the list with a generic name
            return [...knownLanguages, { code: currentLanguage, name: `Language (${currentLanguage})` }];
        }
        return knownLanguages;
    });

    const changeLanguage = async (lang: string) => {
        setIsLoading(true);
        try {
            // If the selected language is not in our known languages, add it
            if (!languageMap.has(lang)) {
                setAvailableLanguages((prev) => [...prev, { code: lang, name: `Language (${lang})` }]);
            }

            const result = await loadLanguageAsync(lang);
            setIsLoading(false);
            return result;
        } catch (error) {
            console.error('Failed to change language:', error);
            setIsLoading(false);
            return 'en'; // Fallback to English on error
        }
    };

    useEffect(() => {
        const handleLanguageChanged = () => {
            setCurrentLanguage(i18n.language);
        };

        i18n.on('languageChanged', handleLanguageChanged);

        return () => {
            i18n.off('languageChanged', handleLanguageChanged);
        };
    }, [i18n]);

    const contextValue = {
        currentLanguage,
        changeLanguage,
        availableLanguages,
        isLoading,
    };

    return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>;
};
